import { Bell } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { ChannelCheckbox, ChannelHeaderRow, ChannelHeading, NotificationRow } from './ChannelColumns'
import { NotificationSectionCard } from './NotificationSectionCard'
import { Switch } from 'app/components/ui/switch'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useAuthStore } from 'app/stores/StoreContext'
import type { LiveAlertPreferences, LiveAlertSettings } from 'app/types/live-alerts'

type LiveAlertItem = {
  key: `newTextMessage` | `incomingCall`
  title: string
  description: string
}

/**
 * `observer` because the incoming-call source is behind a MobX feature toggle
 * that resolves after mount.
 */
export const LiveAlertsSection = observer(function LiveAlertsSection({
  liveAlerts,
  setLiveAlerts,
  editable,
}: {
  liveAlerts: LiveAlertSettings
  setLiveAlerts: (patch: Partial<LiveAlertPreferences>) => void
  editable: boolean
}): ReactElement | null {
  const { products } = useAuthStore()
  // Literal on purpose: bin/generate-feature-toggle-names.mjs extracts names from
  // these call sites, and a constant would leave the toggle unloaded.
  const callAlertsAvailable = useFeatureToggle(`incoming-call-alerts`)
  const textAlertsAvailable = !!products?.includes(`Inbox`)

  // Never offer a switch for something that cannot happen here. With neither
  // source live there is nothing to configure, so the section stays away.
  if (!textAlertsAvailable && !callAlertsAvailable) return null

  const items: LiveAlertItem[] = [
    textAlertsAvailable && {
      key: `newTextMessage` as const,
      title: `Incoming text messages`,
      description: `Show a card when a text arrives in a location you can see`,
    },
    callAlertsAvailable && {
      key: `incomingCall` as const,
      title: `Ringing calls`,
      description: `Show a card while a call is ringing, with who is calling`,
    },
  ].filter((item): item is LiveAlertItem => !!item)

  return (
    <NotificationSectionCard
      icon={Bell}
      title="Live alerts"
      description="Cards that appear over the app the moment something comes in, wherever you are working."
      enabled={liveAlerts.enabled}
      control={
        <Switch
          aria-label="Live alerts"
          checked={liveAlerts.enabled}
          onCheckedChange={(value) => setLiveAlerts({ enabled: value })}
        />
      }
    >
      <ChannelHeaderRow>
        <ChannelHeading label="Popup" />
        <ChannelHeading label="Sound" />
      </ChannelHeaderRow>
      {items.map((item) => {
        const channel = liveAlerts[item.key]
        const frozen = !liveAlerts.enabled
        return (
          <NotificationRow key={item.key} title={item.title} description={item.description}>
            <ChannelCheckbox
              label="Popup"
              title={item.title}
              checked={channel.popup}
              disabled={frozen}
              onCheckedChange={(value) => setLiveAlerts({ [item.key]: { ...channel, popup: value } })}
            />
            <ChannelCheckbox
              label="Sound"
              title={item.title}
              checked={channel.sound}
              // Only a card appearing plays the chime, so sound without popup is
              // a setting that cannot mean anything.
              disabled={frozen || !channel.popup}
              onCheckedChange={(value) => setLiveAlerts({ [item.key]: { ...channel, sound: value } })}
            />
          </NotificationRow>
        )
      })}
    </NotificationSectionCard>
  )
})
